import { styled } from "@linaria/react"
import { Popover, PopoverAnchor, PopoverContent, PopoverPortal } from "@radix-ui/react-popover"
import { observer } from "mobx-react-lite"


import { Button } from "@/components/Button/Button.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading5 } from "@/components/Heading/Heading.tsx"
import { CloseLight, FigmaRegular, Icon20 } from "@/icons/Icons.ts"
import { dropdownShadow } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

interface FigmaPopupProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  hasAuth: boolean
  connectToFigma: () => void
}


export const FigmaInputPopup = observer(({ isOpen, onOpenChange, hasAuth, connectToFigma }: FigmaPopupProps) => {
  return (
    <Popover open={isOpen}>
      <StyledPopoverAnchor />
      <PopoverPortal>
        <StyledPopoverContent
          className={DropdownMenuStyles.Content}
          align="start"
          side="top"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={() => onOpenChange(false)}
        >
          <CloseButton kind="icon" aria-label="Close" onClick={() => onOpenChange(false)}>
            <CloseLight className={Icon20} />
          </CloseButton>

          <Horizontal gap="8px">
            <FigmaRegular />
            <Vertical gap="16px">
              <Vertical gap="8px">
                {!hasAuth && (
                  <>
                    <Heading5>Connect to Figma</Heading5>
                    <Description>
                      Allow Matter to access your Figma files and components so the agent can generate and refine UI
                      designs for you.
                    </Description>
                  </>
                )}

                {hasAuth && (
                  <>
                    <Heading5>Import a Figma link</Heading5>
                    <Description>
                      Paste a link to provide mockups to the agent. It will help you to achieve more precise design with
                      fewer prompts.
                    </Description>
                  </>
                )}
              </Vertical>

              {!hasAuth && (
                <Vertical alignSelf="flex-start">
                  <ConnectButton kind="secondary" onClick={() => {
                    connectToFigma()
                    onOpenChange(false)
                  }}>Connect to Figma</ConnectButton>
                </Vertical>
              )}
            </Vertical>
          </Horizontal>
        </StyledPopoverContent>
      </PopoverPortal>
    </Popover>
  )
})


const Description = styled.div`
  ${textStyles.smallestFontSize};

  line-height: 119%;

  color: var(--secondary-text-color);
`

const CloseButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 16px;

  width: 20px;
  height: 20px;
`

const ConnectButton = styled(Button)`
  font-weight: 400;
`

const StyledPopoverAnchor = styled(PopoverAnchor)`
  position: absolute;
  bottom: 26px;
  left: 76px;

  width: 0;
  height: 0;
`

const StyledPopoverContent = styled(PopoverContent)`
  width: 344px;
  margin-left: -20px;
  padding: 16px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-20);
  background-color: var(--island-background-color);

  ${dropdownShadow};

  z-index: var(--z-index-dropdown);
`
